import type { ReactNode } from 'react';
import { defineMessages } from 'react-intl';
import { useSecurity } from '../../hooks';
import type { ExecutionReport, Order } from '../../types';
import { CustomerOrder, type CustomerDealSummary, type OrderStrategy } from '../../types';
import { abbreviateId, formattedDay, formattedTime } from '../../utils';
import { HStack } from '../Core';
import { InlineFormattedNumber } from '../FormattedNumber';
import { FormattedMessage } from '../Intl';
import { OrderStatus } from '../Status';
import { Text } from '../Text';
import { Header, OrderNumber, ProfitWrapper, SubTitle, Time, Title } from './styles';

interface OrderCardHeaderProps {
  order: Order | ExecutionReport | CustomerOrder;
  selectedStrategy?: OrderStrategy;
  unifiedLiquidityIndicator?: ReactNode;
  symbolSuffix?: ReactNode;
  counterpartyName?: string;
  customerSummary?: CustomerDealSummary;
}

const messages = defineMessages({
  profit: {
    id: 'OrderCard.profit',
    defaultMessage: 'Profit:',
  },
  customer: {
    id: 'OrderCard.customer',
    defaultMessage: 'Customer',
  },
  id: {
    id: 'OrderCard.id',
    defaultMessage: 'ID',
  },
});

export const OrderCardHeader = ({
  order,
  selectedStrategy,
  unifiedLiquidityIndicator,
  symbolSuffix,
  counterpartyName,
  customerSummary,
}: OrderCardHeaderProps) => {
  const security = useSecurity(order.Symbol);
  const isCustomerOrderInstance = order instanceof CustomerOrder;
  const parentOrderID = isCustomerOrderInstance ? null : order.ParentOrderID;
  const showCounterparty = !!parentOrderID || isCustomerOrderInstance;

  return (
    <Header>
      {(showCounterparty || customerSummary) && (
        <HStack justifyContent="space-between">
          {showCounterparty ? <Title>{counterpartyName || <FormattedMessage {...messages.customer} />}</Title> : null}
          {customerSummary && (
            <ProfitWrapper>
              <HStack gap="spacing4">
                <FormattedMessage {...messages.profit} />
                <InlineFormattedNumber
                  number={customerSummary?.PnL?.[0]?.Amount}
                  currency={customerSummary?.PnL?.[0]?.Currency}
                  align="right"
                />
              </HStack>
            </ProfitWrapper>
          )}
        </HStack>
      )}
      <div>
        <Title>
          <span data-testid="order-symbol">{security?.DisplaySymbol}</span>
          {unifiedLiquidityIndicator}
          {symbolSuffix}
          <SubTitle data-testid="order-strategy">{selectedStrategy?.DisplayName || order.Strategy}</SubTitle>
        </Title>

        <OrderStatus
          ordStatus={order.OrdStatus}
          decisionStatus={order.DecisionStatus}
          cumQty={order.CumQty}
          orderQty={order.OrderQty}
          tooltipText={order.Text}
          startTime={order.StartTime}
          endTime={order.EndTime}
          pricingMode={order.PricingMode}
          isCustomerOrder={order instanceof CustomerOrder}
        />
      </div>
      <div>
        <Time data-testid="order-date">{formattedDay(order.SubmitTime)}</Time>
        <Time data-testid="order-time">{formattedTime(order.SubmitTime)}</Time>
        <Text color="colorTextSubtle">
          <label>
            <FormattedMessage {...messages.id} />{' '}
          </label>
          <OrderNumber title={order.OrderID} data-testid="order-number">
            {abbreviateId(order.OrderID)}
          </OrderNumber>
        </Text>
      </div>
    </Header>
  );
};
